import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { useObservableValue } from '@talos/kyoko';
import Big from 'big.js';
import { every } from 'lodash-es';
import { useMemo } from 'react';
import { asyncScheduler, distinctUntilChanged, filter, scan, tap, throttleTime, type Observable } from 'rxjs';
import { updateUnderlyingAmount } from '../Filter/filterSlice';
import { selectOptionFilter } from '../Filter/selectors';
import type { UnderlyingPriceUpdate } from './useOptionChainData';

/**
 * Pipe that updates underlying price of context and provides the `crossStrike`.
 * The crossStrike is the closest strikeprice of the underlying amount.
 * The crossStrike is used for ATM-filtering and coloring of rows as ITM or OTM.
 * For example, if the possible option strikes for an expiration are 2000 3000 4000 5000 and 6000,
 * and the underlying price is 4666, the closest strike price is 5000. So 5000 is kept as the crossStrike for that expiry.
 *
 * Which means that calls at 2000,3000 and 4000 would be regarded as ITM, and puts at 5000 and 6000 would be regarded as ITM.
 *
 * If user is filtering for atm, they are filtering around the crossStrike. So in the above
 * example, if atm is set to 1, the strikePrices that would be shown are 4000 (5000) 6000,
 * if atm is 2, the strikeprices that would show are 3000 4000 (5000) 6000, excluding 7000 since it does not exist.
 */
export function useCrossStrikeByExpiry({
  underlyingPriceSubscription,
  expiryToStrikePriceToIndex,
  panelID,
}: {
  underlyingPriceSubscription: Observable<
    | {
        underlying: {
          value: string;
          currency: string;
        };
        BaseCurrency: string;
        Expiration: string;
      }
    | undefined
  >;
  expiryToStrikePriceToIndex: Map<string, Map<string, number>>;
  panelID: string;
}): Map<string, string> {
  const selectedCurrency = useAppStateSelector(state => selectOptionFilter(state, panelID)).currency;

  const sortedStrikesByExpiry: Map<string, string[]> = useMemo(() => {
    const internalMap = new Map<string, string[]>();
    const expiries = expiryToStrikePriceToIndex.keys();
    for (const expiry of expiries) {
      const sortedStrikes = Array.from(expiryToStrikePriceToIndex.get(expiry)!.entries())
        .toSorted(([aStrike, aIdx], [bStrike, bIdx]) => {
          return aIdx - bIdx;
        })
        .map(tuple => tuple[0]);
      internalMap.set(expiry, sortedStrikes);
    }
    return internalMap;
  }, [expiryToStrikePriceToIndex]);
  const dispatch = useAppStateDispatch();

  return useObservableValue(
    () => {
      return underlyingPriceSubscription.pipe(
        filter(msg => msg?.BaseCurrency === selectedCurrency),
        // Keep internal map of latest underlying amount per expiry
        scan((memo, msg) => {
          if (!msg) {
            return memo;
          }
          memo.set(msg.Expiration, msg);
          return memo;
        }, new Map<string, UnderlyingPriceUpdate>()),
        throttleTime(250, asyncScheduler, { trailing: true }),
        // Update redux with all underlying amounts
        tap(underlyingMap => {
          for (const underlyingAmount of underlyingMap.values()) {
            dispatch(
              updateUnderlyingAmount({
                value: { value: underlyingAmount.underlying.value, currency: underlyingAmount.underlying.currency },
                expiry: underlyingAmount.Expiration,
                baseCurrency: underlyingAmount.BaseCurrency,
                panelID,
              })
            );
          }
        }),
        // Determine the crossStrike for all expiries
        scan((memo, underlyingMap) => {
          const newMap = new Map<string, string>(memo);

          for (const underlyingAmount of underlyingMap.values()) {
            let crossStrike: string | undefined;
            const expiration = underlyingAmount.Expiration;

            const underlyingPrice = underlyingAmount?.underlying.value;
            if (underlyingPrice) {
              // Find the closest strike by indexing in sorted array and calculating difference.
              const sortedStrikes = sortedStrikesByExpiry.get(underlyingAmount.Expiration) || [];

              const strikeIndex = sortedStrikes.findIndex(row => Big(underlyingPrice).lte(row));
              if (strikeIndex !== -1) {
                const left = sortedStrikes.at(Math.max(strikeIndex - 1, 0));
                const right = sortedStrikes.at(strikeIndex);

                const leftDiff = Big(underlyingPrice)
                  .minus(left || 0)
                  .abs();
                const rightDiff = Big(underlyingPrice)
                  .minus(right || 0)
                  .abs();
                if (leftDiff.lt(rightDiff)) {
                  crossStrike = left;
                } else {
                  crossStrike = right;
                }
              }
            }
            if (crossStrike) {
              newMap.set(expiration, crossStrike);
            }
          }

          return newMap;
        }, new Map<string, string>()),
        // Only update reference to Map if crosses change, this is because we
        // want to keep the reference to the crossStrikeByExpiry map as stable as
        // we can, due to it being a dependency in rowFilter.
        distinctUntilChanged((prevMap, currMap) => {
          const largerMap = prevMap.size > currMap.size ? prevMap : currMap;
          // Important to get keys from the larger map so that all possible keys are checked.
          const keys = Array.from(largerMap.keys());
          return every(keys, key => prevMap.get(key) === currMap.get(key));
        }),
        // Throttle this, to avoid flickering around a crossStrike.
        throttleTime(250, asyncScheduler, { trailing: true })
      );
    },
    [dispatch, underlyingPriceSubscription, sortedStrikesByExpiry, selectedCurrency, panelID],
    new Map<string, string>()
  );
}
